// Styles
import "@mdi/font/css/materialdesignicons.css";
import "vuetify/styles";

import { createVuetify } from "vuetify";
import { VFileUpload } from "vuetify/labs/VFileUpload";

/**
 * Yandle BMS — Royal blue brand + warm stone accents.
 * Semantic colours are driven by CSS variables in styles/themes/.
 */
export const yandleThemeColors = {
  background: "#F8F6F1",
  "background-subtle": "#F3F1EC",
  surface: "#FFFFFF",
  primary: "#1848D8",
  "primary-darken-1": "#1235A8",
  secondary: "#6E6E73",
  accent: "#2060E8",
  error: "#D64545",
  success: "#2F9E62",
  warning: "#C4841A",
  info: "#1848D8",

  "on-background": "#1D1D1F",
  "on-surface": "#6E6E73",

  "text-muted": "#9A9A9F",
  "text-inverse": "#FFFFFF",
  border: "#E8E4DE",
  "border-strong": "#D8D2C8",
  "surface-hover": "#F3F1EC",

  "shell-from": "#1D1D1F",
  "shell-to": "#2A2A2E",
  "shell-mid": "#242428",
  "shell-deep": "#111214",
  "shell-border": "#34353B",
  "shell-surface": "#202126",
  "shell-hover": "#2A2B30",
  "shell-active": "#34353B",

  "primary-muted": "rgba(24, 72, 216, 0.12)",
  "accent-muted": "rgba(32, 96, 232, 0.12)",
  "error-muted": "rgba(214, 69, 69, 0.12)",
  "success-muted": "rgba(47, 158, 98, 0.12)",
  "warning-muted": "rgba(196, 132, 26, 0.12)",
  "info-muted": "rgba(24, 72, 216, 0.1)",

  "table-header": "#F8F6F1",
  "table-row-hover": "rgba(24, 72, 216, 0.05)",
  "table-row-stripe": "rgba(248, 246, 241, 0.65)",
};

export const yandleDarkThemeColors = {
  background: "#111214",
  "background-subtle": "#17181C",
  surface: "#1A1B1F",
  primary: "#3D6EF0",
  "primary-darken-1": "#1848D8",
  secondary: "#A1A1A6",
  accent: "#5A84F2",
  error: "#E06060",
  success: "#3FBF76",
  warning: "#D4A032",
  info: "#5A84F2",

  "on-background": "#F5F5F7",
  "on-surface": "#A1A1A6",

  "text-muted": "#6E6E73",
  "text-inverse": "#FFFFFF",
  border: "#34353B",
  "border-strong": "#454650",
  "surface-hover": "#242428",

  "shell-from": "#0E0F11",
  "shell-to": "#1A1B1F",
  "shell-mid": "#17181C",
  "shell-deep": "#111214",
  "shell-border": "#34353B",
  "shell-surface": "#202126",
  "shell-hover": "#2A2B30",
  "shell-active": "#34353B",

  "primary-muted": "rgba(61, 110, 240, 0.16)",
  "accent-muted": "rgba(90, 132, 242, 0.16)",
  "error-muted": "rgba(224, 96, 96, 0.14)",
  "success-muted": "rgba(63, 191, 118, 0.14)",
  "warning-muted": "rgba(212, 160, 50, 0.14)",
  "info-muted": "rgba(90, 132, 242, 0.14)",

  "table-header": "#1A1B1F",
  "table-row-hover": "rgba(61, 110, 240, 0.08)",
  "table-row-stripe": "rgba(255, 255, 255, 0.02)",
};

const VUETIFY_BRIDGE_CSS = `
.v-application {
  font-family: var(--font-family);
  background: transparent;
}

.v-btn.bg-primary {
  background: var(--gradient-brand);
  color: var(--color-text-inverse);
}

.v-field--variant-outlined .v-field__outline {
  --v-field-border-opacity: 0.7;
}

.v-field--variant-outlined.v-field--focused .v-field__outline {
  --v-field-border-opacity: 1;
  color: var(--color-primary);
}

`;

function injectVuetifyBridge() {
  if (typeof document === "undefined") return;
  const id = "yandle-vuetify-bridge";
  if (document.getElementById(id)) return;
  const el = document.createElement("style");
  el.id = id;
  el.textContent = VUETIFY_BRIDGE_CSS;
  document.head.appendChild(el);
}

injectVuetifyBridge();

export default createVuetify({
  components: { VFileUpload },
  theme: {
    defaultTheme: "yandleLight",
    themes: {
      yandleLight: {
        dark: false,
        colors: yandleThemeColors,
        variables: {
          "font-family": '"Inter", system-ui, sans-serif',
          "border-radius-root": "10px",
        },
      },
      yandleDark: {
        dark: true,
        colors: yandleDarkThemeColors,
        variables: {
          "font-family": '"Inter", system-ui, sans-serif',
          "border-radius-root": "10px",
        },
      },
    },
  },
  defaults: {
    VBtn: { rounded: "lg", elevation: 0, size: "small" },
    VCard: { rounded: "lg", elevation: 0 },
    VTextField: { variant: "outlined", density: "compact", color: "primary", hideDetails: "auto" },
    VSelect: { variant: "outlined", density: "compact", color: "primary", hideDetails: "auto" },
    VAutocomplete: { variant: "outlined", density: "compact", color: "primary", hideDetails: "auto" },
    VTextarea: { variant: "outlined", density: "compact", color: "primary" },
    VChip: { rounded: "lg", size: "small" },
    VDataTable: { density: "compact", hover: true, itemsPerPage: 25 },
    VAlert: { variant: "tonal", density: "compact" },
  },
});
